// models/chat/channel-chat-info.ts

import { SnapshotOut, types } from "mobx-state-tree"

const LinkGroupModel = types.model("LinkGroup", {
  channel_id: types.number,
  group_id: types.union(types.number, types.string),
  group_title: types.string,
  is_active: types.number,
})

export type LinkGroupSnapshot = SnapshotOut<typeof LinkGroupModel>

export type LinkGroupLookup = {
  byChatId?: (id: string) => { id?: number; title?: string; name?: string | null; chat_id?: string } | null | undefined
  byId?: (id: number) => { id?: number; title?: string; name?: string | null; chat_id?: string } | null | undefined
  chats?: {
    get?: (id: string) => { id?: number; title?: string; name?: string | null; chat_id?: string } | null | undefined
    values?: () => Iterable<{ id?: number; title?: string; name?: string | null; chat_id?: string }>
  }
  getChatByMessageRef?: (ref: {
    chat_cid?: string | null
    chat_id?: number | null
  }) => { id?: number; title?: string; name?: string | null; chat_id?: string } | null | undefined
}

function coerceLinkGroupArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === "object") return Object.values(raw as Record<string, unknown>)
  return []
}

export function normalizeLinkGroups(raw: unknown): LinkGroupSnapshot[] {
  return coerceLinkGroupArray(raw)
    .map((item: Record<string, unknown>) => {
      const rawGroupId =
        item.group_id ??
        item.group_cid ??
        item.group_alias ??
        item.alias_id ??
        item.gid ??
        item.chat_cid
      const numericId =
        typeof item.id === "number" && item.id > 0
          ? item.id
          : typeof rawGroupId === "number"
            ? rawGroupId
            : /^\d+$/.test(String(rawGroupId ?? ""))
              ? Number(rawGroupId)
              : null
      const groupId = numericId ?? rawGroupId
      if (!item || groupId == null || groupId === "") return null
      const isActive = item.is_active ?? item.isActive ?? item.active
      return {
        channel_id: Number(item.channel_id ?? item.channelId ?? 0),
        group_id: groupId as number | string,
        group_title: String(
          item.group_title ??
            item.group_name ??
            item.groupName ??
            item.title ??
            item.name ??
            groupId ??
            "",
        ),
        is_active:
          typeof isActive === "boolean" ? (isActive ? 1 : 0) : Number(isActive ?? 0),
      }
    })
    .filter((item): item is LinkGroupSnapshot => item != null)
}

export function extractLinkGroupsFromPayload(data: Record<string, unknown>): unknown {
  const nested = [
    data,
    data.channel_chat_info,
    data.channel_setting,
    data.channelSetting,
    data.setting,
    data.settings,
    data.data,
  ]
  const keys = ["link_groups", "linkGroups", "linked_groups", "game_groups", "groups"]
  for (const source of nested) {
    if (!source || typeof source !== "object") continue
    const record = source as Record<string, unknown>
    for (const key of keys) {
      if (record[key] != null) return record[key]
    }
  }
  return undefined
}

/** 从接口/WS 载荷解析关联群；无 link_groups 字段时返回 null */
export function getLinkGroupsFromPayload(data: unknown): LinkGroupSnapshot[] | null {
  if (!data || typeof data !== "object") return null
  const raw = extractLinkGroupsFromPayload(data as Record<string, unknown>)
  if (raw === undefined || raw === null) return null
  return normalizeLinkGroups(raw)
}

export function mergeLinkGroups(
  ...lists: Array<LinkGroupSnapshot[] | null | undefined>
): LinkGroupSnapshot[] {
  const map = new Map<string, LinkGroupSnapshot>()
  for (const list of lists) {
    if (!list?.length) continue
    for (const group of list) {
      const id = String(group.group_id)
      map.set(id, { ...map.get(id), ...group })
    }
  }
  return Array.from(map.values())
}

export function upsertLinkGroup(
  list: LinkGroupSnapshot[],
  group: LinkGroupSnapshot,
): LinkGroupSnapshot[] {
  const target = String(group.group_id)
  const idx = list.findIndex((g) => String(g.group_id) === target)
  if (idx >= 0) {
    const next = [...list]
    next[idx] = { ...next[idx], ...group }
    return next
  }
  return [...list, group]
}

export function findChatForLinkGroup(
  lookup: LinkGroupLookup,
  raw: string,
): { id?: number; title?: string; name?: string | null; chat_id?: string } | null {
  const key = String(raw).trim()
  if (!key) return null

  let chat =
    lookup.byChatId?.(key) ??
    lookup.chats?.get?.(key) ??
    null

  if (!chat && /^\d+$/.test(key)) {
    chat = lookup.byId?.(Number(key)) ?? null
  }

  if (!chat) {
    chat =
      lookup.getChatByMessageRef?.({
        chat_cid: key,
        chat_id: /^\d+$/.test(key) ? Number(key) : null,
      }) ?? null
  }

  if (!chat && lookup.chats?.values) {
    for (const candidate of lookup.chats.values()) {
      if (String(candidate?.name ?? "") === key) return candidate
    }
  }

  return chat ?? null
}

/** 将用户输入的群别名解析为与 App 一致的服务端 group_id（数字）及群名称 */
export function resolveLinkGroupFromInput(
  lookup: LinkGroupLookup,
  channelId: number,
  input: string | number,
  partial?: Partial<LinkGroupSnapshot>,
): LinkGroupSnapshot {
  const raw = String(input).trim()
  const chat = findChatForLinkGroup(lookup, raw)
  const numericId =
    (typeof partial?.group_id === "number" ? partial.group_id : null) ??
    (typeof partial?.group_id === "string" && /^\d+$/.test(partial.group_id)
      ? Number(partial.group_id)
      : null) ??
    chat?.id ??
    (/^\d+$/.test(raw) ? Number(raw) : null)

  return {
    channel_id: channelId,
    group_id: numericId ?? raw,
    group_title: String(
      partial?.group_title ??
        chat?.title ??
        chat?.name ??
        raw,
    ),
    is_active: Number(partial?.is_active ?? 0),
  }
}

export function reresolveLinkGroups(
  lookup: LinkGroupLookup,
  channelId: number,
  groups: LinkGroupSnapshot[],
): LinkGroupSnapshot[] {
  const map = new Map<string, LinkGroupSnapshot>()
  for (const group of groups) {
    const resolved = resolveLinkGroupFromInput(lookup, channelId, group.group_id, group)
    const key = String(resolved.group_id)
    const prev = map.get(key)
    map.set(
      key,
      prev
        ? {
            ...prev,
            ...resolved,
            is_active: Math.max(Number(prev.is_active), Number(resolved.is_active)),
          }
        : resolved,
    )
  }
  return Array.from(map.values())
}

export function isAlreadyLinkedGroupError(message: string): boolean {
  const msg = String(message ?? "").trim()
  if (!msg) return false
  return (
    /已经被?关联/.test(msg) ||
    /已被关联/.test(msg) ||
    /已录入/.test(msg) ||
    /已存在/.test(msg) ||
    /already\s*link/i.test(msg) ||
    (/群/.test(msg) && /关联/.test(msg) && /已/.test(msg))
  )
}

export const ChannelChatInfoModel = types
  .model("ChannelChatInfo")
  .props({
    chat_id: types.number,
    collect_bots: types.optional(types.array(types.frozen()), []),
    compensate_bots: types.optional(types.array(types.frozen()), []),
    link_groups: types.optional(types.array(LinkGroupModel), []),
    owner_percentage: types.maybeNull(types.union(types.number, types.string)),
    official_percentage: types.maybeNull(types.union(types.number, types.string)),
    total_amount: types.maybeNull(types.union(types.number, types.string)), // 总担保资金
    official_balance_cny: types.optional(types.union(types.number, types.string), 0), // 当前担保资金（展示）
  })
  .actions((self) => ({
    update(data: Partial<typeof self> & Record<string, unknown>) {
      if (data.collect_bots) self.collect_bots.replace(data.collect_bots)
      if (data.compensate_bots) self.compensate_bots.replace(data.compensate_bots)
      const linkGroups = extractLinkGroupsFromPayload(data)
      if (linkGroups) self.link_groups.replace(normalizeLinkGroups(linkGroups))
      if (data.owner_percentage !== undefined)
        self.owner_percentage = data.owner_percentage

      if (data.official_percentage !== undefined)
        self.official_percentage = data.official_percentage

      if (data.total_amount !== undefined)
        self.total_amount = data.total_amount

      const balance =
        data.official_balance_cny ??
        data.official_balance ??
        data.balance_cny
      if (balance !== undefined) self.official_balance_cny = balance
    },
    updateLinkGroupActive(group_id: number | string, is_active: number) {
      const target = String(group_id)
      const group = self.link_groups.find((g) => String(g.group_id) === target)
      if (group) group.is_active = is_active
    },
    replaceLinkGroups(raw: unknown) {
      self.link_groups.replace(normalizeLinkGroups(raw))
    },
  }))
  export type ChannelChatInfo = SnapshotOut<typeof ChannelChatInfoModel>
